
import React, { useState, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ReferenceLine,
  ReferenceArea, ResponsiveContainer, Legend
} from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { useIsMobile } from '@/hooks/use-mobile';
import { CycleData, HormoneType, hormoneColors, phaseColors } from '@/utils/cycle-tracking';
import { prepareChartData, calculatePhaseRanges, findTodayPosition } from './chartDataUtils';
import { formatXAxisTick, formatTooltipLabel } from './formatUtils';
import { PhaseLegend } from './PhaseLegend';
import { ChartHeader } from './ChartHeader';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';

interface HormoneChartDisplayProps {
  cycleData: CycleData;
  selectedHormones: HormoneType[];
  showPhases: boolean;
}

export const HormoneChartDisplay: React.FC<HormoneChartDisplayProps> = ({
  cycleData,
  selectedHormones,
  showPhases
}) => {
  const [highlightedPhase, setHighlightedPhase] = useState<string | null>(null);
  const isMobile = useIsMobile();
  
  // Prepare data for the chart
  const chartData = prepareChartData(cycleData);
  
  // Create phase ranges for visualization
  const phaseRanges = calculatePhaseRanges(chartData);
  
  // Find today's position in the cycle
  const todayDay = findTodayPosition(cycleData);
  
  // Calculate minimum width for the chart based on data length
  const minChartWidth = Math.max(chartData.length * 30, isMobile ? 500 : 800);
  
  // Custom formatter for x-axis ticks to prevent overlapping on mobile
  const formatTickCallback = useCallback((value: string) => {
    return formatXAxisTick(value, isMobile, chartData);
  }, [isMobile, chartData]);
  
  // Chart configuration
  const chartConfig = selectedHormones.reduce((acc, hormone) => {
    acc[hormone] = { color: hormoneColors[hormone] };
    return acc;
  }, {} as Record<string, { color: string }>);
  
  return (
    <div className="w-full space-y-2">
      <ChartHeader />
      
      <div className={`${isMobile ? 'h-[200px]' : 'h-[350px]'} w-full overflow-hidden`}>
        <ScrollArea className="w-full h-full" scrollHideDelay={0}>
          <div style={{ width: `${minChartWidth}px`, minWidth: '100%', height: '100%' }}>
            <ChartContainer config={chartConfig}>
              <LineChart
                data={chartData}
                margin={isMobile ? 
                  { top: 10, right: 20, left: -20, bottom: 40 } : 
                  { top: 20, right: 30, left: 0, bottom: 70 }}
                onMouseLeave={() => setHighlightedPhase(null)}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" opacity={0.5} />
                
                {/* Cycle Phases */}
                {showPhases && phaseRanges.map((range, idx) => (
                  <ReferenceArea 
                    key={`phase-${idx}`}
                    x1={range.start - 0.5} 
                    x2={range.end + 0.5}
                    stroke="none"
                    fill={phaseColors[range.phase as keyof typeof phaseColors]}
                    fillOpacity={highlightedPhase === range.phase ? 0.4 : 0.2}
                    onMouseEnter={() => setHighlightedPhase(range.phase)}
                  />
                ))}
                
                {/* Current Day Marker */}
                {todayDay && !isMobile && (
                  <ReferenceLine 
                    x={todayDay} 
                    stroke="#FF6B6B" 
                    strokeWidth={2} 
                    strokeDasharray="3 3"
                    label={{ 
                      value: 'Today', 
                      position: 'bottom',
                      fill: '#FF6B6B',
                      fontSize: isMobile ? 8 : 10
                    }}
                  />
                )}
                
                {/* Phase Labels */}
                {showPhases && !isMobile && phaseRanges.map((range, idx) => (
                  <text
                    key={`label-${idx}`}
                    x={(range.start + range.end) / 2}
                    y={330}
                    textAnchor="middle"
                    fill={highlightedPhase === range.phase ? "#000" : "#666"}
                    style={{ fontSize: '10px', fontWeight: highlightedPhase === range.phase ? 'bold' : 'normal' }}
                  >
                    {range.phase.charAt(0).toUpperCase() + range.phase.slice(1)}
                  </text>
                ))}
                
                <XAxis 
                  dataKey="date" 
                  angle={isMobile ? -45 : -45} 
                  textAnchor="end"
                  height={isMobile ? 40 : 60}
                  tick={{ fontSize: isMobile ? 8 : 10 }}
                  interval={isMobile ? 1 : 0}
                  tickFormatter={formatTickCallback}
                  axisLine={{ strokeWidth: isMobile ? 0.5 : 1 }}
                  tickLine={{ strokeWidth: isMobile ? 0.5 : 1 }}
                />
                <YAxis 
                  domain={[0, 100]} 
                  tickCount={isMobile ? 3 : 6} 
                  tick={{ fontSize: isMobile ? 8 : 10 }}
                  width={isMobile ? 20 : 40}
                  axisLine={{ strokeWidth: isMobile ? 0.5 : 1 }}
                  tickLine={{ strokeWidth: isMobile ? 0.5 : 1 }}
                  label={!isMobile ? { 
                    value: 'Hormone Levels', 
                    angle: -90, 
                    position: 'insideLeft',
                    style: { textAnchor: 'middle', fontSize: 10 }
                  } : undefined}
                />
                
                <ChartTooltip 
                  content={
                    <ChartTooltipContent 
                      labelFormatter={(label) => formatTooltipLabel(label, chartData)}
                    />
                  }
                />
                
                <Legend 
                  verticalAlign="top" 
                  height={isMobile ? 24 : 36}
                  iconSize={isMobile ? 8 : 10}
                  wrapperStyle={{ 
                    paddingTop: isMobile ? '5px' : '10px', 
                    fontSize: isMobile ? '9px' : '12px' 
                  }}
                />
                
                {/* Hormone Lines */}
                {selectedHormones.map(hormone => (
                  <Line 
                    key={hormone}
                    type="monotone"
                    dataKey={hormone}
                    name={hormone.charAt(0).toUpperCase() + hormone.slice(1)}
                    stroke={hormoneColors[hormone]}
                    strokeWidth={isMobile ? 1.5 : 2}
                    dot={{ 
                      r: isMobile ? 3 : 4, 
                      strokeWidth: isMobile ? 1 : 2,
                      fill: 'white',
                      stroke: hormoneColors[hormone]
                    }}
                    activeDot={{ r: isMobile ? 4 : 6, stroke: 'white', strokeWidth: 1.5 }}
                    connectNulls
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ChartContainer>
          </div>
          {/* Enhanced visible scrollbar for horizontal scrolling */}
          <ScrollBar 
            orientation="horizontal" 
            className="h-2 hover:h-3 transition-all" 
          />
        </ScrollArea>
      </div>
      
      {!isMobile && (
        <PhaseLegend 
          phaseRanges={phaseRanges} 
          highlightedPhase={highlightedPhase} 
          setHighlightedPhase={setHighlightedPhase} 
        />
      )}
    </div>
  );
};
